import React from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import DescriptionIcon from '@mui/icons-material/Description';
import BadgeIcon from '@mui/icons-material/Badge';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PublicIcon from '@mui/icons-material/Public';
import EmailIcon from '@mui/icons-material/Email';
import ShareIcon from '@mui/icons-material/Share';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ChatIcon from '@mui/icons-material/Chat';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import HubIcon from '@mui/icons-material/Hub';

/**
 * Platform capability cards for the marketing landing page.
 * Front = summary; back = capability bullets (from app modules / sidebar).
 */
export const PLATFORM_FEATURES = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    summary: 'Role-aware home with KPIs, shortcuts, and team visibility.',
    icon: <DashboardIcon sx={{ fontSize: 32 }} />,
    details: [
      'Personalized landing after login',
      'Quick links to assigned modules',
      'Marquee announcements and status cues',
      'Manager vs employee views via RBAC'
    ]
  },
  {
    id: 'contacts',
    title: 'Contacts & CRM',
    summary: 'Manage clients, vendors, and professional relationships.',
    icon: <PeopleIcon sx={{ fontSize: 32 }} />,
    details: [
      'Contact profiles with full history',
      'Link contacts to accounts and jobs',
      'Custom fields and visibility rules',
      'Archive, audit, and controlled deletion'
    ]
  },
  {
    id: 'accounts',
    title: 'Accounts',
    summary: 'Client and vendor accounts with governance workflows.',
    icon: <BusinessIcon sx={{ fontSize: 32 }} />,
    details: [
      'Account hierarchy and duplicate detection',
      'Duplicate-request approval flow',
      'Domain status and company-domain rules',
      'My account status for end users'
    ]
  },
  {
    id: 'jobs',
    title: 'Jobs & recruiting',
    summary: 'Requirements, submissions, and public job discovery.',
    icon: <WorkIcon sx={{ fontSize: 32 }} />,
    details: [
      'Job requirements intake and tracking',
      'Bench submissions and placement pipeline',
      'Public query portal for open roles',
      'Jobs reporting and team job views'
    ]
  },
  {
    id: 'documents',
    title: 'Documents',
    summary: 'Resumes, agreements, and compliance file management.',
    icon: <DescriptionIcon sx={{ fontSize: 32 }} />,
    details: [
      'Upload, preview, search, and download',
      'Document types and retention policies',
      'Controlled size limits and metadata',
      'Document backup administration'
    ]
  },
  {
    id: 'employees',
    title: 'Employees & HR',
    summary: 'Employee records, roles, titles, and lifecycle data.',
    icon: <BadgeIcon sx={{ fontSize: 32 }} />,
    details: [
      'Employee profiles linked to user accounts',
      'Job roles, levels, and offered titles',
      'Promotion and HR detail workflows',
      'Integration with timesheets and PTO'
    ]
  },
  {
    id: 'timesheets',
    title: 'Timesheets',
    summary: 'Capture billable and non-billable time by period.',
    icon: <ScheduleIcon sx={{ fontSize: 32 }} />,
    details: [
      'Weekly or period-based entry',
      'Manager review patterns',
      'Tied to accounts, jobs, and employees',
      'Export-friendly structured data'
    ]
  },
  {
    id: 'punchcard',
    title: 'Punch card & attendance',
    summary: 'Clock in/out with idle detection and reporting.',
    icon: <AccessTimeIcon sx={{ fontSize: 32 }} />,
    details: [
      'Auto punch-in on login (configurable)',
      'Idle timeout and auto punch-out',
      'My punch card and team visibility',
      'Punch card and idle-time reports'
    ]
  },
  {
    id: 'training',
    title: 'Training & exams',
    summary: 'Catalogs, learning paths, assessments, and discussions.',
    icon: <SchoolIcon sx={{ fontSize: 32 }} />,
    details: [
      'Training catalog and materials',
      'Standalone exams and question bank',
      'Learner progress and exam attempts',
      'Group discussions and admin publishing'
    ]
  },
  {
    id: 'bench',
    title: 'Bench sales',
    summary: 'Bench pipelines, candidates, and availability.',
    icon: <GroupWorkIcon sx={{ fontSize: 32 }} />,
    details: [
      'Bench definitions and candidate pools',
      'Available-candidates discovery',
      'My benches for consultants',
      'Team bench views for managers'
    ]
  },
  {
    id: 'okr',
    title: 'OKR & performance',
    summary: 'Objectives, key results, scoreboards, and employee maps.',
    icon: <TrackChangesIcon sx={{ fontSize: 32 }} />,
    details: [
      'OKR setup with categories and targets',
      'KR matrix with frequency scoring',
      'My OKRs and team OKR scoreboards',
      'Employee map aligned to objectives'
    ]
  },
  {
    id: 'pto',
    title: 'PTO & leave',
    summary: 'Apply leave and route approvals to managers.',
    icon: <EventAvailableIcon sx={{ fontSize: 32 }} />,
    details: [
      'Employee leave applications',
      'Team PTO approval queue',
      'Admin PTO request oversight',
      'Holiday and org calendar settings'
    ]
  },
  {
    id: 'helpdesk',
    title: 'Help desk',
    summary: 'Internal ticketing with queues and severities.',
    icon: <SupportAgentIcon sx={{ fontSize: 32 }} />,
    details: [
      'New ticket creation and attachments',
      'My tickets and team ticket views',
      'Support queue for agents',
      'Configurable severity levels'
    ]
  },
  {
    id: 'immigration',
    title: 'Immigration',
    summary: 'H1, B1, GC, and E-Verify workflow tracking.',
    icon: <PublicIcon sx={{ fontSize: 32 }} />,
    details: [
      'Process H1 and B1 case lists',
      'Green card (GC) progression',
      'E-Verify existing and new hire flows',
      'Structured immigration workflow definitions'
    ]
  },
  {
    id: 'email-extractor',
    title: 'Email extractor',
    summary: 'Import and mine contacts from mail sources.',
    icon: <EmailIcon sx={{ fontSize: 32 }} />,
    details: [
      'Gmail and provider connections',
      'PST upload and extraction history',
      'Manual import and domain exclusions',
      'Extracted-email review dashboard'
    ]
  },
  {
    id: 'digital-media',
    title: 'Digital media',
    summary: 'Social channel registration and account tracking.',
    icon: <ShareIcon sx={{ fontSize: 32 }} />,
    details: [
      'Social media registration workflows',
      'My accounts for owned profiles',
      'Supports consulting brand compliance',
      'Centralized digital footprint records'
    ]
  },
  {
    id: 'my-team',
    title: 'My team',
    summary: 'Manager tools for reportees, jobs, OKRs, and bench.',
    icon: <SupervisorAccountIcon sx={{ fontSize: 32 }} />,
    details: [
      'Team dashboard and reportees (HR)',
      'My team jobs and bench views',
      'Team OKRs and scoreboards',
      'Camera stream for distributed teams'
    ]
  },
  {
    id: 'chat-camera',
    title: 'Chat & camera',
    summary: 'Team chat plus optional camera monitoring.',
    icon: <ChatIcon sx={{ fontSize: 32 }} />,
    details: [
      'In-app chat hub',
      'Employee camera participation',
      'Admin camera monitor dashboard',
      'Permission-gated watch access'
    ]
  },
  {
    id: 'reports',
    title: 'Reports & activity',
    summary: 'Operational reports and audit-friendly activity logs.',
    icon: <AssessmentIcon sx={{ fontSize: 32 }} />,
    details: [
      'Punch card, jobs, and activity reports',
      'Employee and team idle-time analytics',
      'My activities timeline',
      'Export and management oversight'
    ]
  },
  {
    id: 'security',
    title: 'Security & access',
    summary: 'RBAC, groups, OTP, and approval gates.',
    icon: <SecurityIcon sx={{ fontSize: 32 }} />,
    details: [
      'Users, roles, and groups',
      'User access map visualization',
      'Signup approvals and domain controls',
      'OTP / 2FA and session security'
    ]
  },
  {
    id: 'admin',
    title: 'Admin & platform ops',
    summary: 'Tenant configuration, backups, and customization.',
    icon: <SettingsIcon sx={{ fontSize: 32 }} />,
    details: [
      'Branding, visibility, and sidebar setup',
      'Custom fields and module relationships',
      'DB and document backup administration',
      'Seed data, audit trail, and system info'
    ]
  },
  {
    id: 'public-portal',
    title: 'Public query portal',
    summary: 'Anonymous browse of jobs and training (no login).',
    icon: <SearchIcon sx={{ fontSize: 32 }} />,
    details: [
      'Split and regular layout modes',
      'Theme picker for page atmosphere',
      'Open job and training listings',
      'Marketing landing with career journey'
    ]
  },
  {
    id: 'more-to-come',
    title: 'More to come in future…',
    comingSoon: true,
    summary:
      'Horizontal modules in ERP4C today — vertical industry apps next, all on the same platform core.',
    icon: <HubIcon sx={{ fontSize: 32 }} />,
    details: [
      'Horizontal app (ERP4C): shared modules for consulting and professional services',
      'Vertical apps: industry editions built on top of the horizontal base',
      'Planned verticals include ERP4Hospital, ERP4Hotels, ERP4Restaurants, ERP4Manufacturing, ERP4Doctors, and more',
      'Vertical modules extend ERP4C without rebuilding core HR, security, or admin',
      'One architecture — horizontal foundation, vertical specialization'
    ]
  }
];

/** Distinct card colors for the landing flip grid (cycles if more features than entries). */
export const FEATURE_CARD_PALETTE = [
  { front: '#eef2ff', back: '#e0e7ff', border: '#a5b4fc', text: '#1e1b4b', muted: '#4338ca', icon: '#4f46e5', shadow: '0 12px 32px rgba(79, 70, 229, 0.28)' },
  { front: '#fce7f3', back: '#fbcfe8', border: '#f9a8d4', text: '#831843', muted: '#be185d', icon: '#db2777', shadow: '0 12px 32px rgba(219, 39, 119, 0.26)' },
  { front: '#ecfeff', back: '#cffafe', border: '#67e8f9', text: '#164e63', muted: '#0e7490', icon: '#0891b2', shadow: '0 12px 32px rgba(8, 145, 178, 0.26)' },
  { front: '#ecfdf5', back: '#d1fae5', border: '#6ee7b7', text: '#064e3b', muted: '#047857', icon: '#059669', shadow: '0 12px 32px rgba(5, 150, 105, 0.26)' },
  { front: '#fff7ed', back: '#ffedd5', border: '#fdba74', text: '#7c2d12', muted: '#c2410c', icon: '#ea580c', shadow: '0 12px 32px rgba(234, 88, 12, 0.26)' },
  { front: '#fef3c7', back: '#fde68a', border: '#fcd34d', text: '#78350f', muted: '#b45309', icon: '#d97706', shadow: '0 12px 32px rgba(217, 119, 6, 0.26)' },
  { front: '#f5f3ff', back: '#ede9fe', border: '#c4b5fd', text: '#4c1d95', muted: '#6d28d9', icon: '#7c3aed', shadow: '0 12px 32px rgba(124, 58, 237, 0.26)' },
  { front: '#eff6ff', back: '#dbeafe', border: '#93c5fd', text: '#1e3a8a', muted: '#1d4ed8', icon: '#2563eb', shadow: '0 12px 32px rgba(37, 99, 235, 0.26)' },
  { front: '#f0fdf4', back: '#dcfce7', border: '#86efac', text: '#14532d', muted: '#15803d', icon: '#16a34a', shadow: '0 12px 32px rgba(22, 163, 74, 0.26)' },
  { front: '#fdf2f8', back: '#fce7f3', border: '#f0abfc', text: '#701a75', muted: '#a21caf', icon: '#c026d3', shadow: '0 12px 32px rgba(192, 38, 211, 0.24)' },
  { front: '#f0f9ff', back: '#e0f2fe', border: '#7dd3fc', text: '#0c4a6e', muted: '#0369a1', icon: '#0284c7', shadow: '0 12px 32px rgba(2, 132, 199, 0.26)' },
  { front: '#fef2f2', back: '#fee2e2', border: '#fca5a5', text: '#7f1d1d', muted: '#b91c1c', icon: '#dc2626', shadow: '0 12px 32px rgba(220, 38, 38, 0.24)' },
  { front: '#f8fafc', back: '#f1f5f9', border: '#cbd5e1', text: '#0f172a', muted: '#475569', icon: '#64748b', shadow: '0 12px 32px rgba(15, 23, 42, 0.18)' },
  { front: '#fff1f2', back: '#ffe4e6', border: '#fda4af', text: '#881337', muted: '#e11d48', icon: '#f43f5e', shadow: '0 12px 32px rgba(244, 63, 94, 0.24)' },
  { front: '#ecfccb', back: '#d9f99d', border: '#bef264', text: '#365314', muted: '#4d7c0f', icon: '#65a30d', shadow: '0 12px 32px rgba(101, 163, 13, 0.24)' },
  { front: '#ccfbf1', back: '#99f6e4', border: '#5eead4', text: '#134e4a', muted: '#0f766e', icon: '#0d9488', shadow: '0 12px 32px rgba(13, 148, 136, 0.24)' },
  { front: '#fef9c3', back: '#fef08a', border: '#fde047', text: '#713f12', muted: '#a16207', icon: '#ca8a04', shadow: '0 12px 32px rgba(202, 138, 4, 0.22)' },
  { front: '#ede9fe', back: '#ddd6fe', border: '#a78bfa', text: '#3730a3', muted: '#5b21b6', icon: '#6d28d9', shadow: '0 12px 32px rgba(109, 40, 217, 0.24)' },
  { front: '#e0e7ff', back: '#c7d2fe', border: '#818cf8', text: '#312e81', muted: '#4338ca', icon: '#6366f1', shadow: '0 12px 32px rgba(99, 102, 241, 0.26)' },
  { front: '#cffafe', back: '#a5f3fc', border: '#22d3ee', text: '#155e75', muted: '#0891b2', icon: '#06b6d4', shadow: '0 12px 32px rgba(6, 182, 212, 0.24)' },
  { front: '#dcfce7', back: '#bbf7d0', border: '#4ade80', text: '#166534', muted: '#16a34a', icon: '#22c55e', shadow: '0 12px 32px rgba(34, 197, 94, 0.24)' },
  { front: '#ffedd5', back: '#fed7aa', border: '#fb923c', text: '#9a3412', muted: '#ea580c', icon: '#f97316', shadow: '0 12px 32px rgba(249, 115, 22, 0.24)' },
  { front: '#fae8ff', back: '#f5d0fe', border: '#e879f9', text: '#581c87', muted: '#9333ea', icon: '#a855f7', shadow: '0 12px 32px rgba(168, 85, 247, 0.24)' }
];
